/**
 * Copyright 2020 (c) Dominik Basner, Sotec GmbH (for VDW e.V.)
 * Copyright 2020 (c) Fabian Wilhelm, Sotec GmbH (for VDW e.V.)
 * Copyright 2020-2022 (c) Christian von Arnim, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2021 (c) Moritz Walker, ISW University of Stuttgart (for umati and VDW e.V.)
 */
import { IMachine, IPosition } from '@umati/dashboard-types';

const findFairs = /(\w{1,})\s(\w?.{1,3})\s([\w-]{1,6})/g;
const findOffsite = /^(([SN])\s((?:90|[0-8][0-9]|[0-9])\.\d{1,10}))\s(([WE])\s((?:180|[0-1][0-7][0-9]|0?[0-9]?[0-9])\.\d{1,10}))$/g;

class MachineHandling {
  static deserializeMachinesFromBackendRequest (data) {
    const deserializedMachines: IMachine[] = [];
    if (data && data.Machines) {
      for (const machine of data.Machines) {
        machine.Data = JSON.parse(machine.Data);
        deserializedMachines.push(machine);
      }
    }

    for (const machine of deserializedMachines) {
      machine.Online = false;
    }
    return this.filterMachines(deserializedMachines);
  }

  static getNamespaceUri (machine) {
    const url = machine.Path;
    const urlParts = url.split('/');
    return urlParts[urlParts.length - 1];
  }

  static filterMachines (currentMachines : IMachine[] | null) {
    const newMachinesList = currentMachines ?? [];
    const filteredMachines = MachineHandling.splitLocation(newMachinesList);
    return filteredMachines;
  }

  static splitLocation (machines: IMachine[]) {
    for (const machineIndex in machines) {
      const machine = machines[machineIndex];
      if (machine?.Data?.Location?.split) {
        const locations = machine.Data.Location.split('/');
        const positions: IPosition = {};
        for (let location of locations) {
          location = location.trim();
          const count = (location.match(/\s/g) || []).length;
          if (count === 2) {
            findFairs.lastIndex = 0;
            const foundFair = findFairs.exec(location);
            if (foundFair) {
              const fairName = foundFair[1].toLowerCase();
              positions[fairName] = { hall: foundFair[2], booth: foundFair[3] };
            }
          } else if (count === 3) {
            findOffsite.lastIndex = 0;
            const foundOffsite = findOffsite.exec(location);
            if (foundOffsite && foundOffsite.length === 7) {
              const lat = Number(foundOffsite[3]) * (foundOffsite[2] === 'N' ? 1 : -1);
              const long = Number(foundOffsite[6]) * (foundOffsite[5] === 'E' ? 1 : -1);
              positions.offsite = { lat, lng: long };
              positions.offsiteClean = { lat: foundOffsite[1], lng: foundOffsite[4] };
            }
          }
        }
        machines[machineIndex].Positions = positions;
      }
    }
    return machines;
  }

  static isInSet (machine: IMachine, setOfMachineIds: Set<string>): boolean {
    return setOfMachineIds.has(machine.MachineId);
  }

  static setFavorite (machines: IMachine[], favorites: string[]) {
    const favSet = new Set<string>(favorites);
    for (const machineIndex in machines) {
      machines[machineIndex].Favorite = MachineHandling.isInSet(machines[machineIndex], favSet);
    }
    return machines;
  }

  static setOnline (machines: IMachine[], onlineMachines: string[]) {
    if (!Array.isArray(onlineMachines)) {
      return [];
    }
    const onlineSet = new Set<string>(onlineMachines);
    for (const machineIndex in machines) {
      machines[machineIndex].Online = MachineHandling.isInSet(machines[machineIndex], onlineSet);
    }
    return machines;
  }

  static updateList (currentMachines : IMachine[] | null | undefined, newMachines: IMachine[]): IMachine[] {
    const currentMachinesMap = {};
    if(!Array.isArray(currentMachines)) {
      currentMachines = [];
    }

    for (const machineIt in currentMachines) {
      const machine = currentMachines[machineIt];
      const id = this.getMachineId(machine);
      currentMachinesMap[id] = machine;
    }

    for (const machineIt in newMachines) {
      const machine = newMachines[machineIt];
      if (!machine.Data) {
        continue;
      }
      const id = this.getMachineId(machine);
      currentMachinesMap[id] = { ...currentMachinesMap[id], ...machine };
    }

    return Object.values(currentMachinesMap);
  }

  static getMachineId (machine) {
    return machine.MachineId;
  }

  static getMachineName (machine?: IMachine): string {
    const machineIdentification = machine?.Data;
    const model = machineIdentification?.Model && (typeof machineIdentification?.Model === 'object' && machineIdentification?.Model !== null ? String(machineIdentification.Model.text) : String(machineIdentification.Model));
    const productInstanceUri = machineIdentification?.ProductInstanceUri ? String(machineIdentification?.ProductInstanceUri) : undefined;
    return model || productInstanceUri || 'Undefined';
  }

  static getMachineIdFromTopic (topicMachineId: string, clientId?: string): string {
    if (clientId && clientId !== 'umati') {
      return 'clientId=' + clientId + ';' + topicMachineId;
    }
    return topicMachineId;
  }
}
export default MachineHandling;
